import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

export interface ConceptCsvOptions {
  includeValidationFields?: boolean;
  includeConceptId?: boolean;
  includeStandard?: boolean;
  includeVocabularyId?: boolean;
}

/**
 * Converts concepts to CSV format for different use cases
 * @param concepts Array of concepts to convert
 * @param options Configuration for which fields to include
 * @returns CSV content as string
 */
export const conceptsToCsv = (
  concepts: IsValidCreateUploadResponse[],
  options: ConceptCsvOptions = {}
): string => {
  const {
    includeValidationFields = false,
    includeConceptId = false,
    includeStandard = false,
    includeVocabularyId = false,
  } = options;

  // Build headers based on options
  const headers: string[] = [];
  if (includeConceptId) headers.push('concept_id');
  headers.push('concept_name', 'concept_code', 'domain_id');
  if (includeVocabularyId) headers.push('vocabulary_id');
  if (includeStandard) headers.push('standard');
  if (includeValidationFields) headers.push('valid');

  // Escape double quotes per RFC 4180: " → ""
  const esc = (val: string): string => `"${val.replace(/"/g, '""')}"`;

  // Build CSV rows
  const rows = concepts.map((concept) => {
    const row: string[] = [];

    if (includeConceptId) {
      row.push(esc(String(concept.conceptId || '')));
    }
    row.push(esc(concept.conceptName || ''));
    row.push(esc(concept.conceptCode || ''));
    row.push(esc(concept.domainId || ''));

    if (includeVocabularyId) {
      row.push(esc(concept.vocabularyId || '--'));
    }

    if (includeStandard) {
      const standard = concept.isValid && concept.standardConcept === 'S' ? 'Yes' : 'No';
      row.push(esc(standard));
    }

    if (includeValidationFields) {
      const isValid = concept.isValid ? 'Valid' : 'Invalid';
      row.push(esc(isValid));
    }

    return row.join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

/**
 * Creates a File object from concepts data for upload purposes
 * Uses the same format as the validation results download for consistency
 * @param concepts Array of concepts
 * @param filename Name for the file
 * @returns File object
 */
export const createConceptsFile = (
  concepts: IsValidCreateUploadResponse[],
  filename = 'concepts.csv'
): File => {
  const csvContent = conceptsToCsv(concepts, {
    includeValidationFields: true,
    includeConceptId: true,
    includeStandard: true,
    includeVocabularyId: true,
  });

  const blob = new Blob([csvContent], { type: 'text/csv' });
  return new File([blob], filename, { type: 'text/csv' });
};

/**
 * Creates CSV data for export purposes with all validation fields
 * Uses the same format as createConceptsFile for consistency
 * @param concepts Array of concepts
 * @returns Object with filename and CSV content
 */
export const createExportCsvData = (
  concepts: IsValidCreateUploadResponse[],
  conceptGroupName: string
): { filename: string; csvContent: string } => {
  const filename = `${conceptGroupName}-validation-results.csv`;
  const csvContent = conceptsToCsv(concepts, {
    includeValidationFields: true,
    includeConceptId: true,
    includeStandard: true,
    includeVocabularyId: true,
  });

  return { filename, csvContent };
};
